'use client';

import { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';

/**
 * Design-import 6 — light/dark toggle for the auth split-screen top bar.
 *
 * The `(auth)` routes are unauthenticated, so there is no session theme to
 * persist against (unlike the in-app `UserMenu`, which PATCHes
 * `/users/me/theme`). This toggle is therefore a purely client-side cosmetic
 * convenience: on mount it adopts the OS color-scheme preference, and clicking
 * flips the `.dark` class on the document root (the same class Tailwind's
 * `darkMode: ['class']` keys off). It touches no auth state.
 */
function prefersDarkScheme(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(window.matchMedia?.('(prefers-color-scheme: dark)').matches);
}

export function AuthThemeToggle() {
  // Lazy initializer adopts the OS preference once, on the client, without a
  // setState-in-effect (which the React compiler lint rule forbids). The first
  // client render therefore already reflects the user's system scheme.
  const [dark, setDark] = useState<boolean>(prefersDarkScheme);

  // Reflect state onto the document root (the class Tailwind's dark mode keys
  // off). Runs after every change, including the initial mount.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', dark);
  }, [dark]);

  return (
    <button
      type="button"
      onClick={() => setDark((d) => !d)}
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-subtle hover:bg-hover hover:text-foreground transition-colors"
    >
      {dark ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  );
}
